<script setup>
import { computed, onMounted, reactive, watch, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import EmptyState from '../../../../components/widgets/EmptyState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DuplicateInboxBanner from './channels/instagram/DuplicateInboxBanner.vue';
import EmailInboxFinish from './channels/emailChannels/EmailInboxFinish.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const qrCodes = reactive({
  whatsapp: '',
  messenger: '',
  telegram: '',
});

const currentInbox = computed(() =>
  store.getters['inboxes/getInbox'](route.params.inbox_id)
);

// Use useInbox composable with the inbox ID
const {
  isAWhatsAppCloudChannel,
  isATwilioChannel,
  isASmsInbox,
  isALineChannel,
  isAnEmailChannel,
  isAWhatsAppChannel,
  isAFacebookInbox,
  isATelegramChannel,
  isATwilioWhatsAppChannel,
} = useInbox(route.params.inbox_id);

const isBaileysChannel = computed(() => {
  return isAWhatsAppChannel.value && currentInbox.value?.provider === 'baileys';
});

const baileysConnectionStatus = computed(() => {
  return currentInbox.value?.provider_config?.connection_status || 'waiting';
});

const baileysQrCode = computed(() => {
  return currentInbox.value?.provider_config?.qr_code;
});

const hasDuplicateInstagramInbox = computed(() => {
  const instagramId = currentInbox.value.instagram_id;
  const facebookInbox =
    store.getters['inboxes/getFacebookInboxByInstagramId'](instagramId);

  return (
    currentInbox.value.channel_type === INBOX_TYPES.INSTAGRAM && facebookInbox
  );
});

const shouldShowWhatsAppWebhookDetails = computed(() => {
  return (
    isAWhatsAppCloudChannel.value &&
    currentInbox.value.provider_config?.source !== 'embedded_signup'
  );
});

const isWhatsAppEmbeddedSignup = computed(() => {
  return (
    isAWhatsAppCloudChannel.value &&
    currentInbox.value.provider_config?.source === 'embedded_signup'
  );
});

const message = computed(() => {
  if (isATwilioChannel.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isASmsInbox.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.SMS.BANDWIDTH.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isALineChannel.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isAWhatsAppCloudChannel.value && shouldShowWhatsAppWebhookDetails.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (currentInbox.value.web_widget_script) {
    return t('INBOX_MGMT.FINISH.WEBSITE_SUCCESS');
  }

  if (isWhatsAppEmbeddedSignup.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.FINISH.WHATSAPP_QR_INSTRUCTION'
    )}`;
  }

  return t('INBOX_MGMT.FINISH.MESSAGE');
});

async function generateQRCode(platform, identifier) {
  if (!identifier || !identifier.trim()) {
    // eslint-disable-next-line no-console
    console.warn(`Invalid identifier for ${platform} QR code`);
    return;
  }

  try {
    const platformUrls = {
      whatsapp: id => `https://wa.me/${id}`,
      messenger: id => `https://m.me/${id}`,
      telegram: id => `https://t.me/${id}`,
    };

    const url = platformUrls[platform](identifier);
    const qrDataUrl = await QRCode.toDataURL(url);
    qrCodes[platform] = qrDataUrl;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error generating ${platform} QR code:`, error);
    qrCodes[platform] = '';
  }
}

async function generateQRCodes() {
  if (!currentInbox.value) return;

  // WhatsApp (both Cloud and Twilio)
  if (currentInbox.value.phone_number && isAWhatsAppChannel.value) {
    // For Twilio WhatsApp, phone_number format is "whatsapp:+1234567890"
    // Extract just the phone number part for QR code generation
    const phoneNumber = currentInbox.value.phone_number.replace(
      'whatsapp:',
      ''
    );
    await generateQRCode('whatsapp', phoneNumber);
  }

  // Facebook Messenger
  if (currentInbox.value.page_id && isAFacebookInbox.value) {
    await generateQRCode('messenger', currentInbox.value.page_id);
  }

  // Telegram
  if (isATelegramChannel.value && currentInbox.value.bot_name) {
    await generateQRCode('telegram', currentInbox.value.bot_name);
  }
}

const isGeneratingBaileysQr = ref(false);
const baileysQrError = ref(false);
const baileysQrErrorMessage = ref('');
let baileysQrTimeoutId = null;

const clearBaileysQrTimeout = () => {
  if (baileysQrTimeoutId) {
    clearTimeout(baileysQrTimeoutId);
    baileysQrTimeoutId = null;
  }
};

const generateBaileysQRCode = async () => {
  isGeneratingBaileysQr.value = true;
  baileysQrError.value = false;
  baileysQrErrorMessage.value = '';
  clearBaileysQrTimeout();
  try {
    await window.axios.post(
      `/api/v1/accounts/${route.params.accountId}/inboxes/${route.params.inbox_id}/generate_baileys_qr`
    );
    // Start a 30s timeout waiting for the QR code to arrive via webhook
    baileysQrTimeoutId = setTimeout(() => {
      if (
        !baileysQrCode.value &&
        baileysConnectionStatus.value !== 'connected'
      ) {
        baileysQrError.value = true;
        baileysQrErrorMessage.value =
          'O serviço Baileys não respondeu a tempo. Verifique se o serviço está rodando e tente novamente.';
      }
    }, 30000);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to generate Baileys QR code:', error);
    baileysQrError.value = true;
    baileysQrErrorMessage.value =
      error?.response?.data?.error ||
      'Falha ao solicitar QR Code. Verifique se o serviço Baileys está acessível.';
  } finally {
    isGeneratingBaileysQr.value = false;
  }
};

// Clear timeout when QR code arrives or device connects
watch(baileysQrCode, newVal => {
  if (newVal) {
    clearBaileysQrTimeout();
    baileysQrError.value = false;
  }
});
watch(baileysConnectionStatus, newVal => {
  if (newVal === 'connected') {
    clearBaileysQrTimeout();
    baileysQrError.value = false;
  }
});

// Watch for currentInbox changes and regenerate QR codes when available
watch(
  currentInbox,
  newInbox => {
    if (newInbox) {
      generateQRCodes();
    }
  },
  { immediate: true }
);

onMounted(() => {
  generateQRCodes();
});
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <DuplicateInboxBanner
      v-if="hasDuplicateInstagramInbox"
      :content="$t('INBOX_MGMT.ADD.INSTAGRAM.NEW_INBOX_SUGGESTION')"
    />
    <EmptyState
      :title="$t('INBOX_MGMT.FINISH.TITLE')"
      :message="isAnEmailChannel && !currentInbox.provider ? '' : message"
      :button-text="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
    >
      <div class="w-full text-center">
        <div class="my-4 mx-auto max-w-[70%]">
          <woot-code
            v-if="currentInbox.web_widget_script"
            :script="currentInbox.web_widget_script"
          />
        </div>
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isATwilioWhatsAppChannel"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>
        <div
          v-if="shouldShowWhatsAppWebhookDetails"
          class="w-[50%] max-w-[50%] ml-[25%]"
        >
          <p class="mt-8 font-medium text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.WEBHOOK_URL') }}
          </p>
          <woot-code lang="html" :script="currentInbox.callback_webhook_url" />
          <p class="mt-8 font-medium text-n-slate-11">
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.WEBHOOK_VERIFICATION_TOKEN'
              )
            }}
          </p>
          <woot-code
            lang="html"
            :script="currentInbox.provider_config.webhook_verify_token"
          />
        </div>
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isALineChannel"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isASmsInbox"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>
        <EmailInboxFinish
          v-if="isAnEmailChannel && !currentInbox.provider"
          :inbox="currentInbox"
          :inbox-id="$route.params.inbox_id"
        />
        <div
          v-if="isBaileysChannel"
          class="flex flex-col gap-3 items-center mt-8"
        >
          <div
            v-if="baileysConnectionStatus === 'connected'"
            class="text-center"
          >
            <span class="text-n-green-500 font-bold text-lg">{{
              $t('BAILEYS.CONNECTED_TITLE')
            }}</span>
            <p class="mt-2 text-sm text-n-slate-9">
              {{ $t('BAILEYS.CONNECTED_SUBTITLE') }}
            </p>
          </div>

          <div
            v-else-if="baileysQrCode"
            class="text-center flex flex-col items-center"
          >
            <h3 class="font-bold text-n-slate-12">
              {{ $t('BAILEYS.SCAN_QR_TITLE') }}
            </h3>
            <p class="mt-2 text-sm text-n-slate-9 mb-4">
              {{ $t('BAILEYS.SCAN_QR_INSTRUCTIONS') }}
              <strong>{{ $t('BAILEYS.SCAN_QR_CONNECTED_DEVICES') }}</strong>
              &gt;
              <strong>{{ $t('BAILEYS.SCAN_QR_LINK_DEVICE') }}</strong>
              {{ $t('BAILEYS.SCAN_QR_POINT_CAMERA') }}
            </p>
            <div
              class="rounded-lg shadow outline-1 outline-n-strong outline p-2 bg-white inline-block"
            >
              <img
                :src="baileysQrCode"
                alt="Baileys WhatsApp QR Code"
                class="rounded-lg size-64"
              />
            </div>
            <p
              class="mt-4 text-xs font-semibold text-n-brand-500 animate-pulse"
            >
              {{ $t('BAILEYS.WAITING_CONNECTION') }}
            </p>
          </div>

          <div
            v-else-if="
              baileysConnectionStatus === 'connecting' && !baileysQrCode
            "
            class="text-center flex flex-col items-center gap-3"
          >
            <!-- Error state -->
            <div v-if="baileysQrError" class="flex flex-col items-center gap-3">
              <span class="text-2xl">{{ $t('BAILEYS.WARNING_ICON') }}</span>
              <p class="text-sm font-semibold text-red-600 max-w-sm">
                {{ baileysQrErrorMessage }}
              </p>
              <woot-button
                :is-loading="isGeneratingBaileysQr"
                @click="generateBaileysQRCode"
              >
                {{ $t('BAILEYS.RETRY') }}
              </woot-button>
            </div>
            <!-- Loading state -->
            <div v-else>
              <p class="mt-2 text-sm text-n-slate-9 font-medium animate-pulse">
                {{ $t('BAILEYS.REQUESTING_QR') }}
              </p>
            </div>
          </div>

          <div v-else class="text-center flex flex-col items-center">
            <h3 class="font-bold text-n-slate-12">
              {{ $t('BAILEYS.TRIGGER_CONNECTION_TITLE') }}
            </h3>
            <p class="mt-2 text-sm text-n-slate-9 mb-4">
              {{ $t('BAILEYS.TRIGGER_CONNECTION_SUBTITLE') }}
            </p>
            <woot-button
              :is-loading="isGeneratingBaileysQr"
              @click="generateBaileysQRCode"
            >
              {{ $t('BAILEYS.GENERATE_QR') }}
            </woot-button>
          </div>
        </div>

        <div
          v-if="isAWhatsAppChannel && !isBaileysChannel && qrCodes.whatsapp"
          class="flex flex-col gap-3 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.WHATSAPP_QR_INSTRUCTION') }}
          </p>
          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.whatsapp"
              alt="WhatsApp QR Code"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>
        <div
          v-if="isAFacebookInbox && qrCodes.messenger"
          class="flex flex-col gap-3 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.MESSENGER_QR_INSTRUCTION') }}
          </p>
          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.messenger"
              alt="Messenger QR Code"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>
        <div
          v-if="isATelegramChannel && qrCodes.telegram"
          class="flex flex-col gap-4 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.TELEGRAM_QR_INSTRUCTION') }}
          </p>

          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.telegram"
              alt="Telegram QR Code"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>
        <div class="flex gap-2 justify-center mt-4">
          <router-link
            :to="{
              name: 'settings_inbox_show',
              params: { inboxId: $route.params.inbox_id },
            }"
          >
            <NextButton
              outline
              slate
              :label="$t('INBOX_MGMT.FINISH.MORE_SETTINGS')"
            />
          </router-link>
          <router-link
            :to="{
              name: 'inbox_dashboard',
              params: { inboxId: $route.params.inbox_id },
            }"
          >
            <NextButton
              solid
              teal
              :label="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
            />
          </router-link>
        </div>
      </div>
    </EmptyState>
  </div>
</template>
